import { useRef, useState } from 'react';
import _ from 'lodash';
import { Box, Flex, Heading, Divider, IconButton, Badge, Text, Tooltip, Loader, Label } from '@vibe/core';
import { Workflow, DropdownChevronRight, DropdownChevronDown, CloseSmall, Show, Hide, Expand } from '@vibe/icons';
import { coreSectionDescriptorsByType } from '@gorilla/portal-shared/src/core-sections';
import { type Section as SectionType, type PortalSettings } from '@gorilla/portal-shared/src/schemas';
import { type Board as BoardType } from '@gorilla/common/src/lib/monday-api/api';
import { type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { ConditionsModal } from './ConditionsModal';
import { fieldComponentsByType } from './fields';
import { SectionIcon } from './SectionMeta';
import { SectionLinkedToButton } from './SectionLinkedTo';
import { useBoardSourceResolver } from '../hooks/use-board-source-resolver';
import { useBufferState } from '../hooks/use-buffer-state';
import { ExternalLink } from './ExternalLink';

type SectionProps = {
  clientBoard: BoardType;
  section: SectionType;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  onChangePortalSettings: (portalSettings: PortalSettings) => void;
  onChange: (section: SectionType) => void;
  onEditBoardSourceClick: (boardSourceId: string) => void;
  onShowPlaceholdersModalClick: () => void;
  clientBoardSubitemsBoardId: number | null;
  isOpen: boolean;
  onToggleSectionOpen: () => void;
  screenMode: 'small' | 'medium' | 'large';
  isNewSection: boolean;
  pageId: string;
};

export function Section({
  section,
  onChange,
  portalSettings,
  onChangePortalSettings,
  onEditBoardSourceClick,
  onShowPlaceholdersModalClick,
  clientBoardSubitemsBoardId,
  clientBoard,
  isOpen,
  onToggleSectionOpen,
  screenMode,
  isNewSection,
  pageId,
}: SectionProps) {
  const [showingSetupComplete, setShowingSetupComplete] = useState(() => {
    if (isNewSection && (section.type === 'item-grid' || section.type === 'item-table' || section.type === 'client-item-summary')) {
      return true;
    }
    return false;
  });
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  // Only the field values are buffered while typing. The flush merges them into the latest section, so changes made
  // meanwhile (conditions, hidden) aren't overwritten by a copy of the section taken at the first keystroke.
  const latestSection = useRef(section);
  latestSection.current = section;
  const { bufferState: bufferedFieldValues, setBufferState: setBufferedFieldValues } = useBufferState<SectionType['fieldValues']>(
    (newFieldValues) => onChange({ ...latestSection.current, fieldValues: newFieldValues }),
    700,
  );
  let sectionDescriptor = coreSectionDescriptorsByType[section.type];

  const boardSourceResolverResult = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues: section.fieldValues || {},
  });

  let showingSetupCompleteNormalized = showingSetupComplete;

  if ((section.type === 'item-grid' || section.type === 'item-table') && !boardSourceResolverResult.boardSourceType) {
    // the board source setup was not completed yet
    showingSetupCompleteNormalized = false;
  }

  if (!sectionDescriptor) {
    return (
      <Box
        marginStart="xl"
        marginEnd="xl"
        style={{
          marginTop: '27px',
          marginBottom: '27px',
        }}
      >
        <Text type="text2" weight="normal" align="center" color="primary">
          Section type <strong>[{section.type}]</strong> not supported.
        </Text>
      </Box>
    );
  }

  const fieldValues = bufferedFieldValues || section.fieldValues || {};
  const fields = sectionDescriptor.fields
    .filter((field) => {
      if ('requiresBoardSource' in field && field.requiresBoardSource && !boardSourceResolverResult.boardSourceType) {
        return false;
      }
      return true;
    })
    .map((field) => {
      // @ts-ignore
      const FieldComponent = fieldComponentsByType[field.type];

      if (!FieldComponent) {
        return (
          <Box key={field.id}>
            <p>Field type not supported: {field.type}</p>
          </Box>
        );
      }

      const isBoardSourceField = field.type === 'board-source';

      return (
        <FieldComponent
          key={field.id}
          // @ts-ignore
          value={fieldValues[field.id] as any}
          portalSettings={portalSettings}
          sectionDescriptor={sectionDescriptor}
          sectionFieldValues={fieldValues}
          clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
          // @ts-ignore
          fieldDescriptor={field}
          clientBoard={clientBoard}
          onEditBoardSourceClick={onEditBoardSourceClick}
          onShowPlaceholdersModalClick={onShowPlaceholdersModalClick}
          onChange={(newValue: any, newPortalSettings?: PortalSettings) => {
            setBufferedFieldValues({ ...fieldValues, [field.id]: newValue });

            if (newPortalSettings) {
              onChangePortalSettings(newPortalSettings);
            }
          }}
          screenMode={screenMode}
          isOpen={isOpen}
          pageId={pageId}
        />
      );
    });

  return (
    <Box>
      {showConditionsModal ? (
        <ConditionsModal
          board={clientBoard}
          conditions={section.conditions || []}
          conditionsOperator={section.conditionsOperator}
          isOpen={true}
          onClose={(newConditions, newOperator) => {
            if (newConditions !== null) {
              onChange({
                ...section,
                conditions: newConditions,
                conditionsOperator: newOperator,
              });
            }
            setShowConditionsModal(false);
          }}
          isButtonCondition={false}
        />
      ) : null}
      <Flex
        direction="row"
        justify="start"
        align="stretch"
        style={{
          width: '100%',
          backgroundColor: isOpen ? '#f6f7fb' : 'transparent',
          borderTopLeftRadius: isOpen ? '3px' : 0,
          borderTopRightRadius: isOpen ? '3px' : 0,
        }}
      >
        <Flex
          style={{
            overflow: 'hidden',
            width: '48px',
            borderTopLeftRadius: '3px',
            borderBottomLeftRadius: isOpen ? 0 : '3px',
            backgroundColor: '#f6f7fb',
          }}
        >
          <IconButton
            icon={isOpen ? DropdownChevronDown : DropdownChevronRight}
            kind="tertiary"
            size="small"
            onClick={() => {
              onToggleSectionOpen();
            }}
            className="section__toggle_button"
          />
          <Divider direction="vertical" withoutMargin />
        </Flex>
        <Box
          style={{
            flex: 1,
            paddingTop: '17px',
            paddingRight: '96px',
            paddingBottom: '16px',
            paddingLeft: '18px',
          }}
        >
          <Flex direction="row" align="center" gap={4} style={{ width: '100%' }}>
            <Flex direction="row" align="center" gap={10} style={{ width: 'inherit' }}>
              <SectionIcon sectionType={sectionDescriptor.type} />

              <Tooltip content={`Section ID: ${section.id}`} position="top" showOnDialogEnter={false}>
                <Heading
                  type="h3"
                  weight="bold"
                  align="start"
                  style={{
                    cursor: 'pointer',
                    paddingRight: '5px',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText('#' + section.id);
                  }}
                >
                  {sectionDescriptor.title}
                </Heading>
              </Tooltip>

              {section.hidden && <Label text="Hidden" color="dark" />}

              <div style={{ marginLeft: 'auto' }}>
                <Flex direction="row" align="center" gap={14} style={{ width: 'inherit' }}>
                  <SectionLinkedToButton
                    section={section}
                    sectionDescriptor={sectionDescriptor}
                    portalSettings={portalSettings}
                    fieldValues={fieldValues}
                    screenMode={screenMode}
                    onEditBoardSourceClick={onEditBoardSourceClick}
                    hasErrors={boardSourceResolverResult.errors.length > 0}
                  />

                  {section.conditions && section.conditions.length ? (
                    <Badge
                      // @ts-ignore
                      color="primary"
                    >
                      <IconButton
                        icon={Workflow}
                        kind="secondary"
                        size="small"
                        onClick={() => {
                          setShowConditionsModal(true);
                        }}
                      />
                    </Badge>
                  ) : (
                    <div>
                      <IconButton
                        icon={Workflow}
                        kind="secondary"
                        size="small"
                        onClick={() => {
                          setShowConditionsModal(true);
                        }}
                        tooltipContent="Show section conditionally"
                        tooltipProps={{
                          position: 'top',
                          showOnDialogEnter: false,
                        }}
                      />
                    </div>
                  )}
                </Flex>
              </div>
            </Flex>
          </Flex>
        </Box>
      </Flex>

      {isOpen ? (
        <div className={isOpen ? 'section__content' : 'section__content section__content--collapsed'}>
          <Divider withoutMargin />
          <Box
            padding="xl"
            style={{
              overflow: 'visible',
              position: 'relative',
            }}
            className="section__content__fieldcontainer"
          >
            <Flex gap={32} direction="column" align="stretch" className="section__content__fields">
              {showingSetupCompleteNormalized ? (
                <div
                  className="item--board-source--setupdone"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px 16px 16px 20px',
                    border: '1px solid #aed4fc',
                    borderRadius: '4px',
                    backgroundColor: '#cce5ff',
                  }}
                >
                  <Text type="text2" weight="normal" align="center" color="primary" style={{ marginLeft: 'auto' }}>
                    Now choose which columns clients will see, and enable optional features for this section.
                  </Text>
                  <ExternalLink
                    href="https://getgorilla.app/products/client-portal/docs/features/builder/content-sections"
                    style={{
                      marginRight: 'auto',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      color: '#323338',
                    }}
                  >
                    Learn more
                  </ExternalLink>
                  <IconButton
                    icon={CloseSmall}
                    size="xs"
                    kind="tertiary"
                    onClick={() => {
                      setShowingSetupComplete(false);
                    }}
                  />
                </div>
              ) : null}

              {boardSourceResolverResult.errors.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px 16px 16px 20px',
                    border: '1px solid #ecb7bf',
                    borderRadius: '4px',
                    backgroundColor: '#f4c3cb',
                  }}
                >
                  <Text type="text2" weight="normal" align="center" color="primary" style={{ flex: 1 }}>
                    Warning: {boardSourceResolverResult.errors.map((errorMessage) => errorMessage).join(', ')}
                  </Text>
                </div>
              ) : null}

              {boardSourceResolverResult.isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px 16px 16px 20px',
                  }}
                >
                  <Loader id="loader-xs" size="xs" />
                </div>
              ) : null}

              <Flex
                // 16
                gap={0}
                // column
                direction="row"
                align="stretch"
                className="section__content__fields__list"
                style={{
                  flexWrap: 'wrap', // new
                  position: 'relative',
                }}
              >
                {fields}
              </Flex>
            </Flex>
          </Box>
        </div>
      ) : null}

      {!isOpen ? (
        <div
          style={{
            zIndex: 100,
            position: 'absolute',
            bottom: '0px',
            left: '50%',
            backgroundColor: '#f6f7fb',
            transform: 'translate3d(calc(-50% + 6px), 12px, 0)',
            boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <IconButton
            icon={Expand}
            kind="secondary"
            size="xs"
            onClick={() => {
              onToggleSectionOpen();
            }}
            className="section__content--expand_button"
          />
        </div>
      ) : null}
    </Box>
  );
}
